import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Text,
  Button,
  Flex,
  Collapse,
  Divider,
  Tooltip,
  Skeleton,
} from "@chakra-ui/react";
import {
  Waves,
  Wind,
  TreeDeciduous,
  House,
  Mountain,
  Sun,
  Flame,
  ChevronDown,
  ChevronUp,
  CloudFog,
  Binoculars,
  CloudLightning,
  Ellipsis,
} from "lucide-react";
import { RoughSeaIcon } from "../../../assets/icons/RoughSeaIcon";
import { CreateAlertFormValues } from "../types";
import { useAlertCreationTemplateMessages } from "../hooks";
import { useStores } from "../../../stores/setup/use-store";
import { ConditionGroup } from "stores/data/insightAlerts/insightAlertModel";
import { IEnvironmentalVariable } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { RiskTooltipContent } from "./RiskTooltipContent";

/**
 * Risk type definitions with icons, labels, and backend keys
 * Based on Figma design: https://www.figma.com/design/aOVLn22O9luWTn2CalpBVj/-WEB--i4cast?node-id=2670-54229
 *
 * backendKey: The alert type key used in the backend API (alert-template-config endpoint)
 * descriptionKey: Translation key for the risk description in tooltips
 * labelKey: Translation key for the risk label (internationalized)
 */
export const RISK_TYPES = {
  no_category: {
    id: "no_category",
    labelKey: "riskLabel_noCategory",
    icon: Ellipsis,
    backendKey: "no_category",
    descriptionKey: "riskDescription_noCategory",
  },
  rough_sea: {
    id: "rough_sea",
    labelKey: "riskLabel_roughSea",
    icon: RoughSeaIcon,
    backendKey: "rough_sea",
    descriptionKey: "riskDescription_roughSea",
  },
  flooding: {
    id: "flooding",
    labelKey: "riskLabel_flooding",
    icon: Waves,
    backendKey: "extreme_rain",
    descriptionKey: "riskDescription_extremeRain",
  },
  extreme_wind: {
    id: "extreme_wind",
    labelKey: "riskLabel_extremeWind",
    icon: Wind,
    backendKey: "extreme_wind",
    descriptionKey: "riskDescription_extremeWind",
  },
  tree_fall: {
    id: "tree_fall",
    labelKey: "riskLabel_treeFall",
    icon: TreeDeciduous,
    backendKey: "tree_fall",
    descriptionKey: "riskDescription_treeFall",
  },
  roof_damage: {
    id: "roof_damage",
    labelKey: "riskLabel_roofDamage",
    icon: House,
    backendKey: "roof_damage",
    descriptionKey: "riskDescription_roofDamage",
  },
  landslide: {
    id: "landslide",
    labelKey: "riskLabel_landslide",
    icon: Mountain,
    backendKey: "landslide",
    descriptionKey: "riskDescription_landslide",
  },
  extreme_heat: {
    id: "extreme_heat",
    labelKey: "riskLabel_extremeHeat",
    icon: Sun,
    backendKey: "extreme_heat",
    descriptionKey: "riskDescription_extremeHeat",
  },
  wildfire: {
    id: "wildfire",
    labelKey: "riskLabel_wildfire",
    icon: Flame,
    backendKey: "wildfire",
    descriptionKey: "riskDescription_wildfire",
  },
  fog: {
    id: "fog",
    labelKey: "riskLabel_fog",
    icon: CloudFog,
    backendKey: "fog",
    descriptionKey: "riskDescription_fog",
  },
  low_visibility: {
    id: "low_visibility",
    labelKey: "riskLabel_lowVisibility",
    icon: Binoculars,
    backendKey: "low_visibility",
    descriptionKey: "riskDescription_lowVisibility",
  },
  lightning_storm: {
    id: "lightning_storm",
    labelKey: "riskLabel_lightningStorm",
    icon: CloudLightning,
    backendKey: "lightning_storm",
    descriptionKey: "riskDescription_lightningStorm",
  },
} as const;

export type RiskTypeId = keyof typeof RISK_TYPES;

export interface RiskType {
  id: RiskTypeId;
  labelKey: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  backendKey: string | undefined;
  descriptionKey: string;
}

export const RISK_TYPE_LIST: RiskType[] = Object.values(
  RISK_TYPES,
) as RiskType[];

/**
 * Pre-computed set of all risk IDs except "no_category".
 * Static — derived from RISK_TYPE_LIST which never changes at runtime.
 * Used as a fast path when all categorized risks need to be disabled (e.g., loading state).
 */
const ALL_CATEGORIZED_RISK_IDS = new Set(
  RISK_TYPE_LIST.filter((r) => r.id !== "no_category").map((r) => r.id),
);

interface AlertTypeSelectorProps {
  t: (key: string) => string;
  values: CreateAlertFormValues;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  /** Callback when template conditions are applied (for parent component to track original conditions) */
  onTemplateConditionsApplied?: (conditions: ConditionGroup[]) => void;
  /** Environmental variables for displaying condition details in tooltips */
  environmentalVariables?: IEnvironmentalVariable[];
  /** Original template conditions for comparison - used to determine if user modified conditions */
  originalTemplateConditions?: ConditionGroup[] | null;
  /** Form errors object */
  errors?: any;
  /** Form touched fields object */
  touched?: any;
}

export const AlertTypeSelector: React.FC<AlertTypeSelectorProps> = ({
  t,
  values,
  setFieldValue,
  onTemplateConditionsApplied,
  environmentalVariables,
  originalTemplateConditions,
  errors,
  touched,
}) => {
  const {
    dataStores: { insightAlertStore },
  } = useStores();

  const TEMPLATE_MESSAGES = useAlertCreationTemplateMessages(
    t as (key: React.Key) => string,
  );

  // Track if we've already applied the initial template conditions
  const hasAppliedInitialConditionsRef = useRef(false);

  /**
   * Gets the backend alert type key for a given risk type
   */
  const getBackendKeyForRisk = useCallback((riskId: string):
    | string
    | undefined => {
    const risk = RISK_TYPES[riskId as RiskTypeId];
    return risk?.backendKey;
  }, []);

  /**
   * Apply initial template conditions when component mounts and templates are available.
   * Uses the new getTemplateForRisk getter to find the appropriate template.
   * IMPORTANT: Always deep clone template conditions to prevent mutation of store data
   * IMPORTANT: Skip if conditionGroups already has data (user modified conditions survive remounts)
   */
  useEffect(() => {
    // Only apply once AND only when we have all required data
    if (hasAppliedInitialConditionsRef.current) {
      return;
    }

    // If conditionGroups already has data in Formik, skip — conditions were set previously
    // (either by template on first mount or by user edits). This prevents overwriting
    // user modifications when the component remounts (e.g. navigating between steps).
    if (values.conditionGroups && values.conditionGroups.length > 0) {
      hasAppliedInitialConditionsRef.current = true;
      return;
    }

    const storeMacroRegionOrigin = insightAlertStore.macroRegionOrigin;
    const currentTemplateType = values.templateType;

    // Wait until we have macroRegion loaded and a template type selected
    if (storeMacroRegionOrigin && currentTemplateType) {
      // Skip applying template conditions for "no_category" — let the empty state appear
      if (currentTemplateType === "no_category") {
        hasAppliedInitialConditionsRef.current = true;
        return;
      }

      const backendKey = getBackendKeyForRisk(currentTemplateType);

      if (backendKey) {
        const templateConfig = insightAlertStore.getTemplateForRisk(
          backendKey,
          storeMacroRegionOrigin,
        );

        if (templateConfig?.conditions) {
          // Deep clone to prevent mutation of store data
          const clonedConditions = JSON.parse(
            JSON.stringify(templateConfig.conditions),
          );
          setFieldValue("conditionGroups", clonedConditions, false);
          // Notify parent about applied conditions (for badge/reset logic) - also cloned
          onTemplateConditionsApplied?.(
            JSON.parse(JSON.stringify(templateConfig.conditions)),
          );
          hasAppliedInitialConditionsRef.current = true;
        } else {
          // No template config found for this combination, let user create from scratch
          hasAppliedInitialConditionsRef.current = true;
        }
      } else {
        // No backend key mapping for this risk type, let user create from scratch
        hasAppliedInitialConditionsRef.current = true;
      }
    }
    // NOTE: Don't mark as applied if data isn't ready yet - let useEffect run again when data arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    insightAlertStore.alertTemplates,
    insightAlertStore.customAlertTemplates,
    insightAlertStore.defaultAlertTemplates,
    insightAlertStore.macroRegionOrigin,
    values.templateType,
    setFieldValue,
    getBackendKeyForRisk,
    onTemplateConditionsApplied,
    insightAlertStore,
    // NOTE: values.conditionGroups intentionally excluded — we only read it once on mount
    // to check if conditions already exist. Including it would cause re-runs on every change.
  ]);

  /**
   * Applies the template change - updates form values with new template data
   * Only applies conditions if the user hasn't modified them (conditionsMatchTemplate is true)
   * IMPORTANT: Always deep clone template conditions to prevent mutation of store data
   */
  const applyTemplateChange = useCallback(
    (templateType: string, shouldApplyConditions: boolean) => {
      setFieldValue("templateType", templateType, false);

      // For "no_category":
      // - If user hasn't modified conditions (shouldApplyConditions=true) → clear them so the empty state appears
      // - If user has modified conditions (shouldApplyConditions=false) → keep user's conditions
      if (templateType === "no_category") {
        if (shouldApplyConditions) {
          setFieldValue("conditionGroups", undefined, false);
          // Reset the original template reference so the next risk switch
          // correctly detects conditions as "unmodified" and applies the new template
          onTemplateConditionsApplied?.([] as ConditionGroup[]);
        }
        // Leave title/description empty so placeholders are visible
        setFieldValue("customTitle", "", false);
        setFieldValue("customDescription", "", false);
        return;
      }

      // Get the backend key for this risk type
      const backendKey = getBackendKeyForRisk(templateType);

      // Use backendKey to look up template messages (they use backend keys like "extreme_wind")
      // Only look up if we have a valid backendKey, since TEMPLATE_MESSAGES is keyed by backend keys
      const template = backendKey ? TEMPLATE_MESSAGES[backendKey] : undefined;

      // Determine effective title/description:
      // - Prefer backend template (when available)
      // - Fallback to risk type label as title and empty description when no template exists
      const riskType = RISK_TYPES[templateType as RiskTypeId];
      const fallbackTitle = riskType?.labelKey ? t(riskType.labelKey) : "";
      const fallbackDescription = riskType?.descriptionKey
        ? t(riskType.descriptionKey)
        : "";
      const effectiveTitle = template?.title ?? fallbackTitle;
      const effectiveDescription = template?.description ?? fallbackDescription;

      // Always set title and description (may be empty for unknown risk types)
      setFieldValue("customTitle", effectiveTitle, false);
      setFieldValue("customDescription", effectiveDescription, false);

      // Only apply template conditions if user hasn't modified their conditions
      if (shouldApplyConditions) {
        const storeMacroRegionOrigin = insightAlertStore.macroRegionOrigin;

        if (storeMacroRegionOrigin && backendKey) {
          const templateConfig = insightAlertStore.getTemplateForRisk(
            backendKey,
            storeMacroRegionOrigin,
          );
          if (templateConfig?.conditions) {
            // Deep clone to prevent mutation of store data
            const clonedConditions = JSON.parse(
              JSON.stringify(templateConfig.conditions),
            );
            setFieldValue("conditionGroups", clonedConditions, false);
            // Notify parent about applied conditions (also cloned)
            onTemplateConditionsApplied?.(
              JSON.parse(JSON.stringify(templateConfig.conditions)),
            );
          }
        }
      } else {
        // User has modified conditions - notify parent about the new template conditions
        // so the "back to suggestion" button knows what to reset to
        const storeMacroRegionOrigin = insightAlertStore.macroRegionOrigin;
        if (storeMacroRegionOrigin && backendKey) {
          const templateConfig = insightAlertStore.getTemplateForRisk(
            backendKey,
            storeMacroRegionOrigin,
          );
          if (templateConfig?.conditions) {
            // Update the original template reference for the new risk type (deep cloned)
            onTemplateConditionsApplied?.(
              JSON.parse(JSON.stringify(templateConfig.conditions)),
            );
          }
        }
      }
    },
    [
      setFieldValue,
      TEMPLATE_MESSAGES,
      insightAlertStore,
      getBackendKeyForRisk,
      onTemplateConditionsApplied,
      t,
    ],
  );

  /**
   * Normalize conditions for comparison by removing IDs and sorting
   * This ensures we compare only the actual condition values
   * Operators are normalized to lowercase for case-insensitive comparison
   */
  const normalizeConditionsForComparison = useCallback(
    (groups: ConditionGroup[] | null | undefined): string => {
      if (!groups || groups.length === 0) return "";

      const normalized = groups.map((group) => ({
        groupOperator: (group.groupOperator || "and").toLowerCase(),
        conditionOperator: (group.conditionOperator || "and").toLowerCase(),
        conditions: [...group.conditions]
          .sort((a, b) => {
            if (a.environmentalVariableId !== b.environmentalVariableId) {
              return a.environmentalVariableId - b.environmentalVariableId;
            }
            return a.value - b.value;
          })
          .map((condition) => ({
            environmentalVariableId: condition.environmentalVariableId,
            comparisonOperator: condition.comparisonOperator,
            value: condition.value,
          })),
      }));

      return JSON.stringify(normalized);
    },
    [],
  );

  /**
   * Handler called when user clicks on a template type.
   * Directly applies the template without confirmation modal.
   * Only applies new conditions if user hasn't modified the current ones.
   */
  const handleTemplateTypeChange = useCallback(
    (templateType: string) => {
      // Don't do anything if clicking on already selected type
      if (templateType === values.templateType) {
        return;
      }

      // Calculate match at the moment of click to ensure we have the latest values
      let shouldApplyConditions = true;

      // Check if user has existing conditions
      const hasExistingConditions =
        values.conditionGroups &&
        values.conditionGroups.length > 0 &&
        values.conditionGroups.some(
          (group) => group.conditions && group.conditions.length > 0,
        );

      if (originalTemplateConditions && originalTemplateConditions.length > 0) {
        // We have original template conditions to compare against
        const currentNormalized = normalizeConditionsForComparison(
          values.conditionGroups,
        );
        const originalNormalized = normalizeConditionsForComparison(
          originalTemplateConditions,
        );
        shouldApplyConditions = currentNormalized === originalNormalized;
      } else if (hasExistingConditions) {
        // No original template conditions (e.g., coming from no_category),
        // but user has existing conditions - preserve them
        shouldApplyConditions = false;
      }

      applyTemplateChange(templateType, shouldApplyConditions);
    },
    [
      values.templateType,
      values.conditionGroups,
      applyTemplateChange,
      originalTemplateConditions,
      normalizeConditionsForComparison,
    ],
  );

  // State for expanded/collapsed view
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate how many cards fit in the first row based on container width
  const containerRef = useRef<HTMLDivElement>(null);
  const [cardsPerRow, setCardsPerRow] = useState(5);

  useEffect(() => {
    const calculateCardsPerRow = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const cardWidth = 120; // card width in px
        const gap = 16; // gap between cards in px
        // Calculate how many cards fit: (containerWidth + gap) / (cardWidth + gap)
        const count = Math.floor((containerWidth + gap) / (cardWidth + gap));
        // Ensure at least 3 cards and at most total cards
        setCardsPerRow(Math.max(3, Math.min(count, RISK_TYPE_LIST.length)));
      }
    };

    calculateCardsPerRow();
    window.addEventListener("resize", calculateCardsPerRow);
    return () => window.removeEventListener("resize", calculateCardsPerRow);
  }, []);

  // Visible risks based on how many fit in one row
  const visibleRisks = RISK_TYPE_LIST.slice(0, cardsPerRow);
  const hiddenRisks = RISK_TYPE_LIST.slice(cardsPerRow);

  // Check if selected risk is in the hidden section - if so, expand automatically
  // Note: We calculate hiddenRisks inside the effect to avoid dependency on the derived array
  useEffect(() => {
    const selectedRiskId = values.templateType as RiskTypeId;
    const hiddenRisksForCheck = RISK_TYPE_LIST.slice(cardsPerRow);
    const isSelectedInHidden = hiddenRisksForCheck.some(
      (risk) => risk.id === selectedRiskId,
    );
    if (isSelectedInHidden && !isExpanded) {
      setIsExpanded(true);
    }
  }, [values.templateType, cardsPerRow, isExpanded]);

  /**
   * Pre-computed map of risk ID → template conditions.
   * Resolves template lookups (.find()) once per macro region/template change,
   * rather than on every render call in both disabledRisks and renderRiskCard.
   *
   * Fixes a correctness issue: the previous getConditionsForRisk useCallback
   * depended on [insightAlertStore] (stable ref), so disabledRisks wouldn't
   * recompute when macroRegionOrigin or templates changed.
   */
  const conditionsByRiskId = useMemo((): Map<string, ConditionGroup[]> => {
    const map = new Map<string, ConditionGroup[]>();
    const macroRegion = insightAlertStore.macroRegionOrigin;
    if (!macroRegion) return map;

    for (const risk of RISK_TYPE_LIST) {
      if (!risk.backendKey || risk.id === "no_category") continue;
      const template = insightAlertStore.getTemplateForRisk(
        risk.backendKey,
        macroRegion,
      );
      // Store conditions (or empty array if template has no conditions)
      // so disabledRisks can distinguish "has template with conditions" from
      // "has template without conditions" and "no template at all"
      if (template) {
        map.set(risk.id, template.conditions || []);
      }
    }

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    insightAlertStore.macroRegionOrigin,
    insightAlertStore.customAlertTemplates,
    insightAlertStore.defaultAlertTemplates,
  ]);

  /**
   * Set of disabled risk IDs based on environmental variable availability.
   *
   * A risk is disabled when:
   * - Environmental variables are still loading → returns pre-computed ALL_CATEGORIZED_RISK_IDS
   * - Template conditions reference ANY environmentalVariable missing from the available set
   *
   * "no_category" is always enabled.
   */
  const disabledRisks = useMemo((): Set<string> => {
    // Fast path: while loading, disable all categorized risks (avoid iteration)
    if (insightAlertStore.isLoadingEnvironmentalVariables) {
      return ALL_CATEGORIZED_RISK_IDS;
    }

    // Build lookup sets for both name and ID to ensure robust matching
    // The template conditions use environmentalVariable (name) and environmentalVariableId (ID)
    // The load-env-variables endpoint returns internal_name and id
    // Using both ensures we don't incorrectly disable a risk due to naming mismatches
    const availableNames = new Set(
      insightAlertStore.environmentalVariables.map((v) => v.internal_name),
    );
    const availableIds = new Set(
      insightAlertStore.environmentalVariables.map((v) => v.id),
    );

    const disabled = new Set<string>();

    for (const risk of RISK_TYPE_LIST) {
      if (risk.id === "no_category") continue;

      const conditions = conditionsByRiskId.get(risk.id);

      // Disable risk if no template exists for this macro region,
      // or if template has no conditions (empty/missing)
      if (!conditions || conditions.length === 0) {
        disabled.add(risk.id);
        continue;
      }

      const hasUnavailableVar = conditions.some((group) =>
        group.conditions.some(
          (c) =>
            c.environmentalVariable &&
            !availableNames.has(c.environmentalVariable) &&
            !(
              c.environmentalVariableId &&
              availableIds.has(c.environmentalVariableId)
            ),
        ),
      );

      if (hasUnavailableVar) {
        disabled.add(risk.id);
      }
    }

    return disabled;
  }, [
    conditionsByRiskId,
    insightAlertStore.environmentalVariables,
    insightAlertStore.isLoadingEnvironmentalVariables,
  ]);

  /**
   * Auto-deselect the currently selected risk if it becomes disabled
   * (e.g., user changes location and the risk's variables are no longer available)
   *
   * IMPORTANT: Skip deselection while environmental variables are loading.
   * During loading, ALL categorized risks are temporarily disabled to show skeleton cards.
   * Clearing the selection during this transient state would lose user data when the
   * drawer remounts (e.g., returning from sensor selection triggers a forceReload).
   */
  useEffect(() => {
    if (insightAlertStore.isLoadingEnvironmentalVariables) return;
    if (
      values.templateType &&
      disabledRisks.has(values.templateType as string)
    ) {
      setFieldValue("templateType", "", false);
      setFieldValue("conditionGroups", undefined, false);
      setFieldValue("customTitle", "", false);
      setFieldValue("customDescription", "", false);
      onTemplateConditionsApplied?.([] as ConditionGroup[]);
    }
  }, [
    disabledRisks,
    values.templateType,
    setFieldValue,
    onTemplateConditionsApplied,
    insightAlertStore.isLoadingEnvironmentalVariables,
  ]);

  const isLoadingEnvVars = insightAlertStore.isLoadingEnvironmentalVariables;

  const renderSkeletonCard = (index: number) => (
    <Box
      key={`skeleton-${index}`}
      w="120px"
      h="120px"
      borderRadius="8px"
      overflow="hidden"
    >
      <Skeleton
        w="120px"
        h="120px"
        borderRadius="8px"
        startColor="#e8edf2"
        endColor="#f5f7fa"
      />
    </Box>
  );

  const renderRiskCard = (risk: RiskType) => {
    const IconComponent = risk.icon;
    const isSelected = values.templateType === risk.id;
    const isDisabled = disabledRisks.has(risk.id);
    const conditions = conditionsByRiskId.get(risk.id);

    const cardContent = (
      <Box
        as="button"
        type="button"
        onClick={
          isDisabled ? undefined : () => handleTemplateTypeChange(risk.id)
        }
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={isDisabled ? "8px" : "10px"}
        w="120px"
        h="120px"
        p="10px"
        flexShrink={0}
        borderRadius="8px"
        border="1px solid"
        borderColor={
          isDisabled ? "#b9c6d3" : isSelected ? "#0884cc" : "#b9c6d3"
        }
        bg={
          isDisabled
            ? "white"
            : isSelected
            ? "rgba(183, 227, 252, 0.32)"
            : "white"
        }
        cursor={isDisabled ? "not-allowed" : "pointer"}
        overflow="hidden"
        transition="all 0.2s"
        _hover={
          isDisabled
            ? {}
            : {
                borderColor: "#0884cc",
                bg: isSelected
                  ? "rgba(183, 227, 252, 0.32)"
                  : "rgba(183, 227, 252, 0.16)",
              }
        }
        _focus={{
          outline: "none",
          boxShadow: isDisabled ? "none" : "0 0 0 2px rgba(8, 132, 204, 0.3)",
        }}
      >
        <Flex direction="column" align="center" justify="center" gap="10px">
          <IconComponent
            size={24}
            color={isDisabled ? "#a2b3c4" : isSelected ? "#0884cc" : "#4a6075"}
          />
          <Text
            fontSize="14px"
            fontWeight="500"
            color={isDisabled ? "#a2b3c4" : isSelected ? "#0884cc" : "#4a6075"}
            textAlign="center"
            whiteSpace="pre-line"
            lineHeight="1.2"
          >
            {t(risk.labelKey)}
          </Text>
        </Flex>
        {isDisabled && (
          <Text
            fontSize="10px"
            fontWeight="500"
            color="#70c7f9"
            textAlign="center"
            lineHeight="18px"
            whiteSpace="nowrap"
          >
            ({t("notAvailable")})
          </Text>
        )}
      </Box>
    );

    // Disabled cards get a white tooltip with risk name + unavailability message
    if (isDisabled) {
      return (
        <Tooltip
          key={risk.id}
          label={
            <Box
              bg="white"
              borderRadius="8px"
              boxShadow="0px 2px 8px rgba(0, 0, 0, 0.25)"
              p="10px"
              minW="200px"
              maxW="240px"
            >
              <Text
                fontSize="12px"
                fontWeight="700"
                color="#242e38"
                lineHeight="16px"
                mb={2}
              >
                {t(risk.labelKey).replace("\n", " ")}
              </Text>
              <Text
                fontSize="12px"
                fontWeight="400"
                color="#242e38"
                lineHeight="16px"
              >
                {t("riskCategoryUnavailable")}
              </Text>
            </Box>
          }
          placement="bottom"
          openDelay={200}
          hasArrow={false}
          bg="transparent"
          p={0}
          borderRadius="8px"
        >
          {cardContent}
        </Tooltip>
      );
    }

    // Enabled cards with backendKey get the detailed conditions tooltip
    if (risk.backendKey) {
      return (
        <Tooltip
          key={risk.id}
          label={
            <RiskTooltipContent
              t={t}
              conditions={conditions}
              environmentalVariables={environmentalVariables}
              descriptionKey={risk.descriptionKey}
            />
          }
          placement="bottom"
          openDelay={300}
          closeDelay={100}
          hasArrow={false}
          bg="transparent"
          p={0}
          borderRadius="8px"
        >
          {cardContent}
        </Tooltip>
      );
    }

    return <React.Fragment key={risk.id}>{cardContent}</React.Fragment>;
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={0}>
        <Text
          fontSize="14px"
          fontWeight="500"
          color="#242e38"
          lineHeight="24px"
        >
          {t("selectRiskCategory")}*
        </Text>
        <Text
          fontSize="14px"
          fontWeight="400"
          color="#4a6075"
          lineHeight="24px"
        >
          {t("selectRiskDescription")}
        </Text>
      </Box>

      {/* Risk cards - first row always visible */}
      <Flex ref={containerRef} gap="16px" flexWrap="wrap" py="16px">
        {isLoadingEnvVars
          ? visibleRisks.map((_, i) => renderSkeletonCard(i))
          : visibleRisks.map(renderRiskCard)}
      </Flex>

      {/* Expandable section with remaining risks */}
      <Collapse in={isExpanded} animateOpacity>
        <Flex gap="16px" flexWrap="wrap" pb="16px">
          {isLoadingEnvVars
            ? hiddenRisks.map((_, i) =>
                renderSkeletonCard(visibleRisks.length + i),
              )
            : hiddenRisks.map(renderRiskCard)}
        </Flex>
      </Collapse>

      {/* Error message when no risk is selected */}
      {errors?.templateType && touched?.templateType && (
        <Text color="red.500" fontSize="sm" mt={2}>
          {t("pleaseSelectRisk")}
        </Text>
      )}

      {/* Toggle button */}
      {hiddenRisks.length > 0 && (
        <Flex direction="column" align="center" gap="8px">
          <Button
            variant="link"
            color="#0884cc"
            fontSize="14px"
            fontWeight="500"
            rightIcon={
              isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />
            }
            onClick={() => setIsExpanded(!isExpanded)}
            _hover={{ textDecoration: "none", opacity: 0.8 }}
          >
            {isExpanded ? t("collapseRiskOptions") : t("seeMoreRiskTypes")}
          </Button>
          {isExpanded && <Divider borderColor="#d1d9e2" w="260px" />}
        </Flex>
      )}
    </Box>
  );
};
